import { resolveRegion } from '@/helpers/travel-esim-regions'

const KEY_RE = /^destinations\[(\d+)\]\[(code|arrival|departure|type)\]$/i

const isValidYmd = (value) => {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false

    const timestamp = Date.parse(`${value}T00:00:00Z`)
    if (isNaN(timestamp)) return false

    return new Date(timestamp).toISOString().slice(0, 10) === value
}

const firstValue = (value) => (Array.isArray(value) ? value[0] : value)

// ISO 3166-1 alpha-2 code, trimmed and upper-cased, or '' when not one.
const isoCode = (value) => {
    const normalized = String(value || '').trim().toUpperCase()
    return /^[A-Z]{2}$/.test(normalized) ? normalized : ''
}

// Normalize one leg group, or null when it can't be used. Destination legs are
// a region name or an ISO country code (sent as-is; the BE resolves it).
// Transit legs are always an ISO country code.
const parseLeg = (group) => {
    const type = String(group.type || '').trim().toLowerCase()
    const arrival_date = isValidYmd(group.arrival) ? group.arrival : ''
    const departure_date = isValidYmd(group.departure) ? group.departure : ''

    if (type === 'transit') {
        const place_code = isoCode(group.code)
        return place_code ? { type, place_code, arrival_date, departure_date } : null
    }

    const code = resolveRegion(group.code) || isoCode(group.code)
    if (!code) return null

    return { type, code, name: code, arrival_date, departure_date }
}

// Parse an itinerary deep link into destination rows. Transit legs are NOT their
// own rows: each is nested under the destination before it (a leading transit
// goes to the first destination) and sent in that leg's `Transits[]`. `origin`
// legs are dropped entirely.
export const parseItineraryQuery = (query) => {
    if (!query || typeof query !== 'object') return []

    const groups = new Map()
    Object.keys(query).forEach(key => {
        const match = key.match(KEY_RE)
        if (!match) return

        const index = Number(match[1])
        const field = match[2].toLowerCase()
        const group = groups.get(index) || {}
        group[field] = firstValue(query[key])
        groups.set(index, group)
    })

    const legs = [...groups.keys()]
        .sort((a, b) => a - b)
        .map(index => parseLeg(groups.get(index)))
        .filter(Boolean)
        // exclude "origin" from destination - business decision
        .filter(leg => leg.type !== 'origin')

    const destinations = []
    let pending_transits = []

    legs.forEach(leg => {
        if (leg.type === 'transit') {
            const transit = {
                place_code: leg.place_code,
                arrival_date: leg.arrival_date,
                departure_date: leg.departure_date,
            }

            const current = destinations[destinations.length - 1]
            if (current) {
                current.transits.push(transit)
            } else {
                pending_transits.push(transit)
            }
            return
        }

        destinations.push({
            code: leg.code,
            name: leg.name,
            arrival_date: leg.arrival_date,
            departure_date: leg.departure_date,
            transits: pending_transits,
        })
        pending_transits = []
    })

    return destinations
}

// True when the query contains ANY `destinations[...]` param — i.e. a deep link
// was attempted, even if it yields no valid rows. Lets the page tell an invalid
// deep link (redirect) apart from plain direct navigation (normal empty form).
export const hasItineraryParams = (query) =>
    !!query &&
    typeof query === 'object' &&
    Object.keys(query).some(key => /^destinations\[/i.test(key))

// Local "YYYY-MM-DD", `days` from today
export const ymdFromToday = (days = 0) => {
    const date = new Date()
    date.setDate(date.getDate() + days)
    const offset = date.getTimezoneOffset()
    return new Date(date.getTime() - offset * 60000).toISOString().slice(0, 10)
}

// Validation shared with prefill
export const todayYmd = () => ymdFromToday(0)

// Earliest allowed arrival date: one day before today. Covers timezone-crossing
// flights where the destination local date is "yesterday" relative to the
// traveller's origin (e.g. NZ → Rarotonga: ~23 hours behind).
export const earliestTripDateYmd = (today = todayYmd()) => {
    const d = new Date(today + 'T00:00:00Z')
    d.setUTCDate(d.getUTCDate() - 1)
    return d.toISOString().slice(0, 10)
}

export const DESTINATION_FIELDS = ['code', 'arrival_date', 'departure_date']

// check_empty: flag empty fields
export const destinationFieldErrorKey = (destination, field, { today = todayYmd(), check_empty = false } = {}) => {
    const { code, arrival_date, departure_date } = destination || {}

    if (field === 'code') {
        return check_empty && !code ? 'travel_esim.error_destination_required' : ''
    }

    if (field === 'arrival_date') {
        if (!arrival_date) return check_empty ? 'travel_esim.error_arrival_required' : ''
        if (arrival_date < earliestTripDateYmd(today)) return 'travel_esim.error_arrival_past'
        return ''
    }

    if (field === 'departure_date') {
        if (!departure_date) return check_empty ? 'travel_esim.error_departure_required' : ''
        if (departure_date < earliestTripDateYmd(today)) return 'travel_esim.error_departure_past'
        if (arrival_date && departure_date < arrival_date) return 'travel_esim.error_departure_before_arrival'
        return ''
    }

    return ''
}

export const isDestinationValid = (destination, today = todayYmd()) =>
    DESTINATION_FIELDS.every(field => !destinationFieldErrorKey(destination, field, { today, check_empty: true }))

export const areDestinationsValid = (destinations, today = todayYmd()) =>
    Array.isArray(destinations)
    && destinations.length > 0
    && destinations.every(destination => isDestinationValid(destination, today))

// Demo trip for the "Choose trip" button: Europe (+ Japan transit), Australia; always in the future
export const buildDemoItineraryQuery = () => ({
    'destinations[0][code]': 'Europe',
    'destinations[0][type]': 'destination',
    'destinations[0][arrival]': ymdFromToday(30),
    'destinations[0][departure]': ymdFromToday(39),
    'destinations[1][code]': 'JP',
    'destinations[1][type]': 'transit',
    'destinations[1][arrival]': ymdFromToday(60),
    'destinations[1][departure]': ymdFromToday(74),
    'destinations[2][code]': 'Australia',
    'destinations[2][type]': 'destination',
    'destinations[2][arrival]': ymdFromToday(74),
    'destinations[2][departure]': ymdFromToday(84),
})

export default parseItineraryQuery
